// Auth service. Wraps the customer-auth subset of /api/v1 and returns parsed
// payloads. The account_id (multi-tenant key) is injected from the store
// context — callers never pass it.
import { apiClient } from '@core/api';
import { endpoints } from '@core/api';
import { useStoreContextStore } from '@core/store/storeContextStore';
import { useAuthStore } from '@core/store/authStore';

const AUTH_LOG_PREFIX = '[auth-flow]';

function mask(value) {
  if (!value || typeof value !== 'string') return value;
  if (value.length <= 4) return '****';
  return `${value.slice(0, 2)}****${value.slice(-2)}`;
}

function logFlow(step, details) {
  console.info(`${AUTH_LOG_PREFIX} ${step}`, details);
}

function accountId() {
  const id = useStoreContextStore.getState().accountId;
  if (!id) throw new Error('Missing accountId — industry config must provide one');
  return id;
}
export const authService = {
  /** @param {{ email?: string, mobile?: { calling_code?: string, number?: string }, password: string }} input */
  async login({ email, mobile, password }) {
    const resp = await apiClient.post(endpoints.auth.customerLogin, {
      data: { account_id: accountId(), email, mobile, password },
    });
    useAuthStore.getState().setSession(resp);
    return resp;
  },

  /** @param {{ email: string, password: string, first_name: string, last_name?: string, mobile?: object }} input */
  async signup(input) {
    logFlow('signup:request', {
      account_id: accountId(),
      email: input?.email,
      has_mobile: Boolean(input?.mobile),
    });
    return apiClient.post(endpoints.auth.customerSignup, {
      data: { account_id: accountId(), source: 'storefront', ...input },
    });
  },

  /** @param {{ otp: string, token?: string }} input */
  async verifyOtp(input) {
    const token =
      input.token ||
      input.otp_token ||
      input.otpToken ||
      input.verification_token ||
      input.verificationToken ||
      input.reset_token ||
      input.resetToken ||
      '';

    logFlow('verify-otp:request', {
      otp: mask(input?.otp),
      has_token: Boolean(token),
      token: token ? mask(String(token)) : null,
    });

    return apiClient.post(endpoints.auth.verifyOtp, {
      data: {
        otp: String(input?.otp || '').trim(),
        ...(token ? { token: String(token).trim() } : {}),
      },
    });
  },

  /** @param {{ email?: string, mobile?: { calling_code?: string, number?: string } }} input */
  async resendOtp({ email, mobile }) {
    logFlow('resend-otp:request', {
      account_id: accountId(),
      email,
      has_mobile: Boolean(mobile),
    });
    return apiClient.post(endpoints.auth.resendOtp, {
      data: { account_id: accountId(), email, mobile },
    });
  },

  /** @param {{ email?: string, mobile?: { calling_code?: string, number?: string } }} input */
  async forgotPassword({ email, mobile }) {
    logFlow('forgot-password:request', {
      account_id: accountId(),
      email,
      has_mobile: Boolean(mobile),
    });
    return apiClient.post(endpoints.auth.forgotPassword, {
      data: { account_id: accountId(), email, mobile },
    });
  },

  /** @param {{ confirm_password: string, new_password: string, email?: string, mobile?: { calling_code?: string, number?: string } }} input */
  async resetPassword(input) {
    return apiClient.post(endpoints.auth.resetPassword, {
      data: { account_id: accountId(), ...input },
    });
  },

  /**
   * Local-only logout. The Bytz spec doesn't expose a server-side logout for
   * customers; revocation is handled by letting the access token expire.
   */
  logout() {
    useAuthStore.getState().clearSession();
  },
};
